/**
 * useKeyboardShortcuts Hook - Global keyboard shortcut handler
 * Manages command palette (Ctrl/Cmd+K), help modal (?), and navigation shortcuts
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export function useKeyboardShortcuts() {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isShortcutHelpOpen, setIsShortcutHelpOpen] = useState(false);
  const navigate = useNavigate();

  // Track key sequence for "G + X" navigation shortcuts
  const [keySequence, setKeySequence] = useState([]);

  const handleKeyDown = useCallback((e) => {
    // Ignore if typing in an input/textarea/contenteditable
    const target = e.target;
    const isTyping =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.closest('[contenteditable="true"]');

    // Ctrl/Cmd + K: Open command palette (works even in inputs)
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setIsCommandPaletteOpen(true);
      return;
    }

    // If typing in an input, ignore other shortcuts
    if (isTyping) {
      setKeySequence([]);
      return;
    }

    // Escape: Close any open modal
    if (e.key === 'Escape') {
      if (isCommandPaletteOpen) {
        setIsCommandPaletteOpen(false);
        return;
      }
      if (isShortcutHelpOpen) {
        setIsShortcutHelpOpen(false);
        return;
      }
    }

    // ?: Toggle shortcut help
    if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      setIsShortcutHelpOpen(prev => !prev);
      return;
    }

    // Track key sequence for navigation shortcuts (G + X)
    const key = e.key.toLowerCase();
    const newSequence = [...keySequence, key].slice(-2);
    setKeySequence(newSequence);

    // Navigation shortcuts: G + [D/E/S/P]
    if (newSequence[0] === 'g' && newSequence.length === 2) {
      const routes = {
        d: '/organizer',
        e: '/events',
        s: '/schedule',
        p: '/profile',
      };

      if (routes[newSequence[1]]) {
        e.preventDefault();
        navigate(routes[newSequence[1]]);
        setKeySequence([]);
        return;
      }
    }

    // Clear sequence after a timeout
    setTimeout(() => setKeySequence([]), 1000);
  }, [isCommandPaletteOpen, isShortcutHelpOpen, keySequence, navigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    isCommandPaletteOpen,
    setIsCommandPaletteOpen,
    isShortcutHelpOpen,
    setIsShortcutHelpOpen,
    openCommandPalette: () => setIsCommandPaletteOpen(true),
    closeCommandPalette: () => setIsCommandPaletteOpen(false),
    openShortcutHelp: () => setIsShortcutHelpOpen(true),
    closeShortcutHelp: () => setIsShortcutHelpOpen(false),
  };
}
